import {FilterOptionDate, FilterOptionItem, FilterOptionRole, Rank, Role, RoleList, StoredState} from './interfaces'
import tracker from '../../components/tracker/publish'
import * as Chart from 'chart.js'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import QueryParameters from './QueryParameters'

export const normalizeRoles = (roles: RoleList) => {
  const { mainRoles, customRoles } = roles
  let normalizedRoles = []
  const mainRolesTitle = { id: 'main-role-title', name: 'title', value: 'PRIMARY USER ROLE' }
  const customRolesTitle = { id: 'custom-role-title', name: 'title', value: 'CUSTOM USER ROLE' }
  const separator = { id: 'separator', name: 'separator', value: 'separator' }
  normalizedRoles.push(mainRolesTitle)
  normalizedRoles.push(...normalizeUserRoles(mainRoles))
  if (Array.isArray(customRoles)) {
    normalizedRoles.push(separator)
    normalizedRoles.push(customRolesTitle)
    normalizedRoles.push(...normalizeUserRoles(customRoles))
  }

  return normalizedRoles
}

export const normalizeUserRoles = (roles: Array<Role>) => {
  return roles.map((role: Role) => ({
    id: role.id,
    name: role.label,
    value: role.id,
    isMain: role.isMain
  }))
}

export const normalizeUserRanks = (ranks: Array<Rank>) => {
  return ranks.map((rank: Rank) => ({
    id: rank.id,
    name: rank.value,
    value: rank.id
  }))
}

export const normalizeCategories = (categories: any[]) => {
  return categories
    .filter(parent => parent.categories)
    .map(parent => {
      return {
        parentId: parent.groupId,
        parentName: parent.groupName,
        items: parent.categories.map((child: { categoryName: any; categoryId: any }, idx: any) => {
          return {
            id: idx,
            name: child.categoryName,
            value: child.categoryId
          }
        })
      }
    })
}

export const fetchContentTypes = () => {
  const contentTypes = ['conversation', 'question', 'article']
  return contentTypes.map((contentType, idx) => ({
    id: idx,
    name: `${contentType.charAt(0).toUpperCase()}${contentType.slice(1)}`,
    value: contentType === 'conversation' ? 'discussion' : contentType
  }))
}

export const getDefaultDateRange = (presets: Array<FilterOptionDate>, defaultValue: string) => {
  return presets.filter((range: FilterOptionDate) => range.id === defaultValue)[0]
}

export function getSelectedRolesForRequest (userRoles: Array<FilterOptionRole>, selectedRoles: Array<string>, isMainRole = true) {
  return userRoles.filter(item => item.isMain === isMainRole && selectedRoles.includes(item.id.toString())).map(item => item.id.toString())
}

export function trackDashboardUsage (dashboardName: string) {
  tracker.trackEvent('userevent', {
    type: 'Dashboard viewed',
    new_dashboard: true,
    dashboard_name: dashboardName
  })
}

export function previousPeriodDifference (currentCount: number, previousCount: number) {
  if (previousCount === 0) {
    return 0
  }
  return ((currentCount - previousCount) / previousCount) * 100
}

export function getPlaceholderText (list: Array<FilterOptionItem>, selectedList: Array<string>) {
  const selectedListCount = selectedList.length

  if (list.length === 0 || selectedListCount === 0) {
    return 'All'
  }
  let placeholderText = list.find(({ value }: FilterOptionItem) => { return value.toString() === selectedList[0] })?.name
  if (selectedListCount - 1 === 0) {
    return placeholderText
  }
  return `${placeholderText} +${selectedList.length - 1}`
}

export function getCategoryPlaceholder (list: Array<FilterOptionItem>, selectedList: Array<string>) {
  const selectedListCount = selectedList.length
  if (selectedListCount === 0) {
    return {
      text: 'All',
      additionalText: ''
    }
  }
  let placeholderText = list.find(({ value }: FilterOptionItem) => { return value.toString() === selectedList[0] })?.name
  if (selectedListCount - 1 === 0) {
    return {
      text: placeholderText,
      additionalText: ''
    }
  }
  return {
    text: placeholderText,
    additionalText: `+${selectedList.length - 1}`
  }
}

export function getChildCategories (list: any[]) {
  let items: Array<FilterOptionItem> = []
  list.forEach(parent => {
    items.push(parent.items)
  })
  return items.flat()
}

export function createDefaultMetricItem () {
  return {
    count: 0,
    previousCount: 0,
    percentageDifference: 0
  }
}

export const BRAND_BLUE = '#0069FF'

export const BRAND_GREY = '#2B3346'

export const BRAND_BORDER_GREY = '#D5D6DA'

export const BRAND_YELLOW = '#FFD000'

export function getDefaultSelectedDate () {
  return {
    id: 'custom_range',
    label: 'Custom range',
    startDate: new Date(),
    endDate: new Date()
  }
}
export function displayLegendsWithMarginBottom () {
  // @ts-ignore Legend does not exist on Chart.
  Chart.Legend.prototype.afterFit = function () {
    if (this.height !== 0) {
      this.height = this.height + 16
    }
  }
}

export const DEFAULT_DATE_RANGE_ID = 'last_thirty_days'

export function initializeDateRangeInSessionStorage (dateRange: FilterOptionDate) {
  if (!window.sessionStorage.getItem(DATE_RANGE_STORAGE_KEY)) {
    window.sessionStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify(dateRange))
  }
}

export function updateDateRangeInSessionStorage (selectedDate: FilterOptionDate) {
  if (selectedDate.id === 'custom_range') {
    const dateRange = {
      id: selectedDate.id,
      label: selectedDate.label,
      startDate: AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate),
      endDate: AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate)
    }
    window.sessionStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify(dateRange))
    return
  }

  window.sessionStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify(selectedDate))
}

export const DATE_RANGE_STORAGE_KEY = 'selectedDateRange'

export function getStartAndEndDate () {
  let startDate = AnalyticsDateFormatter.getMonthStartDate()
  let endDate = AnalyticsDateFormatter.getYesterday()
  let item = window.sessionStorage.getItem(DATE_RANGE_STORAGE_KEY)
  const sessionDateRange = item ? JSON.parse(item) : null
  if (sessionDateRange !== null && DEFAULT_DATE_RANGE_ID !== sessionDateRange.id) {
    startDate = new Date(sessionDateRange.startDate)
    endDate = new Date(sessionDateRange.endDate)
  }

  return { initialStartDate: startDate, initialEndDate: endDate }
}

/**
 * Checks for date range in the query parameter and saves to session storage if it does not exist
 * Reads date range from session storage if no query parameters are found
 * @param storedState
 * @param defaultDateRange
 * @param selectedPresetDateRangeId
 */
export function dateRangeForDisplay (storedState: StoredState, defaultDateRange: FilterOptionDate, selectedPresetDateRangeId: string) {
  let updatedState = Object.assign({}, storedState)
  let dateFrom = new Date(defaultDateRange.startDate)
  let dateTill = new Date(defaultDateRange.endDate)
  let selectedPresetId = defaultDateRange.id
  let selectedDateRange = defaultDateRange
  const queryParameters = new QueryParameters()
  const isQueryParameterSet = queryParameters.get('date_range') !== undefined
  updatedState.presetDateRangeId = defaultDateRange.id
  updatedState.selectedDateRange = defaultDateRange

  if (selectedPresetDateRangeId === 'custom_range') {
    const queryParameters = new QueryParameters()
    dateFrom = new Date(queryParameters.get('date_from'))
    dateTill = new Date(queryParameters.get('date_till'))
    defaultDateRange = {
      id: 'custom_range',
      label: 'Custom range',
      startDate: dateFrom,
      endDate: dateTill
    }
    selectedDateRange = defaultDateRange
    selectedPresetId = selectedPresetDateRangeId
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = defaultDateRange
  }

  initializeDateRangeInSessionStorage(defaultDateRange)

  if (!isQueryParameterSet) {
    let item = window.sessionStorage.getItem(DATE_RANGE_STORAGE_KEY)
    const sessionDateRange = item ? JSON.parse(item) : null
    if (sessionDateRange !== null && DEFAULT_DATE_RANGE_ID !== sessionDateRange.id) {
      dateFrom = new Date(sessionDateRange.startDate)
      dateTill = new Date(sessionDateRange.endDate)
      selectedPresetId = sessionDateRange.id
      selectedDateRange = sessionDateRange
      updatedState.presetDateRangeId = sessionDateRange.id
      updatedState.selectedDateRange = sessionDateRange
    }
  } else {
    updateDateRangeInSessionStorage(defaultDateRange)
  }
  return { dateFrom, dateTill, selectedDateRange, selectedPresetId, updatedState }
}
